import { Component, Suspense, type ReactNode } from 'react';
import { createBrowserHistory } from 'history';
import { Route, Routes, unstable_HistoryRouter as HistoryRouter, useLocation } from 'react-router-dom';
import { APP_ROUTES } from 'app/router/routes';
import PageHitTracker from 'features/telemetry/public/PageHitTracker';

const history = createBrowserHistory();

const RouteLoadingFallback = () => (
    <div
        role="status"
        aria-live="polite"
        style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '100vh',
            width: '100%',
        }}
    >
        Loading Unity Intelligence…
    </div>
);

// A screen's code that can't be fetched (offline, or an open tab after a deploy replaced its files) shows how to recover,
// not a blank page. A new route clears it.
class RouteLoadBoundary extends Component<{ children: ReactNode; resetKey: string }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: Error) {
        console.error('Route failed to load', error);
    }

    componentDidUpdate(previous: { resetKey: string }) {
        if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false });
    }

    render() {
        if (!this.state.failed) return this.props.children;
        return <div role="alert" style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 12,
            justifyContent: 'center', minHeight: '100vh', width: '100%' }}>
            This page couldn't load. Check your connection, then reload.
            <button type="button" onClick={() => window.location.reload()}>Reload</button>
        </div>;
    }
}

const RoutedPages = () => {
    const { pathname } = useLocation();
    return <RouteLoadBoundary resetKey={pathname}>
        <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
                {APP_ROUTES.map(({ path, element }) => (
                    <Route key={path} path={path} element={element} />
                ))}
            </Routes>
        </Suspense>
    </RouteLoadBoundary>;
};

const AppRouter = () => (
    <HistoryRouter
        history={history as any}
        future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
    >
        <PageHitTracker />
        <RoutedPages />
    </HistoryRouter>
);

export default AppRouter;
